/**
 * Auto-découvert par `apps/web/src/routes/modules.ts` via :
 *   import.meta.glob('../../../modules/*\/web/route.tsx', { eager: true })
 *
 * Parcours « factures : l'humain n'arbitre que les exceptions » (mockup
 * validé 23/09, spec `docs/etudes/spec-factures-exceptions-2026-09-23.md`) :
 *  - `analyse-docs` : file d'attente des factures (seul onglet visible) ;
 *  - `analyse-docs/:id` : une facture → vue exceptions ; tout autre document
 *    (confirmation, BL, facture encore en lecture) → page document existante ;
 *  - `analyse-docs/commandes/:idComFour` : la commande « problème en cours »
 *    (litiges, note de crédit, prix de vente à revoir) ;
 *  - `analyse-docs/fournisseurs/:idFour/tarif` : « Nouveau tarif fournisseur » ;
 *  - `analyse-docs/prix-de-vente` : liste GLOBALE « Prix de vente à revoir ».
 *
 * L'ancienne page « Vérifier ligne à ligne » (`analyse-docs/:id/verification`)
 * est supprimée (spec §4) : les factures passent par le parcours exceptions.
 *
 * `badge` (compteur de l'onglet) : factures à arbitrer, lu sur
 * `GET /factures/compteurs` — le même appel porte `commandesProblemeEnCours`
 * pour l'onglet Commandes (déclaré par `herbo-commandes`).
 */
import { lazy, Suspense } from 'react';
import type { ModuleRoute } from '@herbo/module-route';

const FileAttenteFacturesPage = lazy(() => import('./pages/FileAttenteFactures'));
const DocumentPage = lazy(() => import('./pages/Document'));
const CommandeLitigesPage = lazy(() => import('./pages/CommandeLitiges'));
const TarifFournisseurPage = lazy(() => import('./pages/TarifFournisseur'));
const PrixVenteARevoirPage = lazy(() => import('./pages/PrixVenteARevoir'));

const URL_COMPTEURS_FACTURES = '/api/herbo-analyse-docs/factures/compteurs';

function LoadingFallback({ label }: { label: string }): JSX.Element {
  return (
    <div className="p-6 text-sm text-slate-500" role="status" aria-live="polite">
      {label}
    </div>
  );
}

const fileAttenteRoute: ModuleRoute = {
  path: 'analyse-docs',
  label: 'Factures fournisseurs',
  icon: 'receipt',
  badge: { url: URL_COMPTEURS_FACTURES, champ: 'facturesAArbitrer', ton: 'warn' },
  element: (
    <Suspense fallback={<LoadingFallback label="Chargement des factures…" />}>
      <FileAttenteFacturesPage />
    </Suspense>
  ),
};

const documentRoute: ModuleRoute = {
  path: 'analyse-docs/:id',
  label: 'Factures fournisseurs',
  icon: 'receipt',
  hidden: true,
  element: (
    <Suspense fallback={<LoadingFallback label="Chargement de la facture…" />}>
      <DocumentPage />
    </Suspense>
  ),
};

const commandeRoute: ModuleRoute = {
  path: 'analyse-docs/commandes/:idComFour',
  label: 'Factures fournisseurs',
  icon: 'receipt',
  hidden: true,
  element: (
    <Suspense fallback={<LoadingFallback label="Chargement de la commande…" />}>
      <CommandeLitigesPage />
    </Suspense>
  ),
};

const tarifRoute: ModuleRoute = {
  path: 'analyse-docs/fournisseurs/:idFour/tarif',
  label: 'Factures fournisseurs',
  icon: 'receipt',
  hidden: true,
  element: (
    <Suspense fallback={<LoadingFallback label="Chargement du tarif…" />}>
      <TarifFournisseurPage />
    </Suspense>
  ),
};

/** Liste GLOBALE « Prix de vente à revoir » (option B, Ben 23/09) ; `?idComFour=N` filtre une commande. */
const prixVenteRoute: ModuleRoute = {
  path: 'analyse-docs/prix-de-vente',
  label: 'Factures fournisseurs',
  icon: 'receipt',
  hidden: true,
  element: (
    <Suspense fallback={<LoadingFallback label="Chargement des prix de vente…" />}>
      <PrixVenteARevoirPage />
    </Suspense>
  ),
};

export default [fileAttenteRoute, prixVenteRoute, documentRoute, commandeRoute, tarifRoute];
